import React from 'react';
import styles from './LineDetails.styles';
import { ILineDetailsHandle, ILineDetailsProperties } from './LineDetails.properties';
import { withStyles } from '@material-ui/core';
import FormView from 'components/FormView';
import ProductReceiptLineDetailsForm from './LineDetails.form';
import SummaryTable from 'components/common/SummaryTable';
import SummaryTableSchema from './LineDetails.summaryTable.schema';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { Cancel as CancelIcon, CheckCircle, QuoteIcon } from '@markinson/uicomponents-v2/SvgIcons/';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { useCalculateProductReceiptLineDetails, useFetchProductReceiptLineDetails, useRetrieveLineDetailsInitialBin, useSetProduceReceiptsLineDetailsQueryData, useUpdateProductReceiptLineDetails } from 'api/productReceipts/productReceipts';
import { usePRContextSelector } from '../ProductReceipts.context';
import AddSerialDialog from 'components/common/AddSerialDialog';
import { useCalculateSerialNumber } from 'api/serialNumber/serialNumber';
import BinLocationsDialog from 'components/common/BinLocationReceiptDialog';
import AddLotDialog from 'components/common/AddLotDialog';

const LineDetails = (props: ILineDetailsProperties, ref: React.Ref<ILineDetailsHandle>) => {
    const { classes, formValues, dirty, destroyForm, resetForm, changeConfirmationDialog } = props;
    const [internalDirty, setInternalDirty] = React.useState<boolean>(false);
    const isDirty = internalDirty || dirty;
    const onCancelClicked = React.useRef(false);
    const params = new URLSearchParams(location.search);

    const { mutateAsync: calculateSerialNumberMutateAsync } = useCalculateSerialNumber();

    const { data: lineDetailsInitialBin, mutateAsync: fetchLineDetailsInitialBin } = useRetrieveLineDetailsInitialBin();

    const [addSerialDialogOpen, setAddSerialDialogOpen] = React.useState(false);
    const [addBinLocationDialogOpen, setAddBinLocationDialogOpen] = React.useState(false);

    // GET PRODUCT RECEIPT CONTEXT
    const ProductReceiptContext = usePRContextSelector<'ProductReceiptContext'>((state) => state.ProductReceiptContext);

    // GET LINE NUMBER
    const LineNumber = Number(params.get('LineNumber') ?? 0);

    // USE RETREIVE PRODUCT RECEIPT LINE DETAILS
    const { data: productReceiptLineDetailsData, isLoading: productReceiptLineDetailsLoading, isFetching: isProductReceiptLineDetailsFetching } = useFetchProductReceiptLineDetails(ProductReceiptContext?.ProductReceiptId, LineNumber);

    const updateProductReceiptLineDetailsMutation = useUpdateProductReceiptLineDetails();

    const calculateLineDetailsMutation = useCalculateProductReceiptLineDetails();

    const setProductReceiptLineDetails = useSetProduceReceiptsLineDetailsQueryData();

    const { setActionBar } = React.useContext(ActionBarContext);

    const { Quantity, BinTracked, ProductId } = productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineDetails || {};

    const { BinLot = [] } = productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject ?? {};

    const [selectedBinLot, setSelectedBinLot] = React.useState(null);

    const [initialbinloaded, setInitialBinLoaded] = React.useState(false);

    const [lotDialogOpen, setLotDialogOpen] = React.useState(false);

    const changeConfirmationDialogDefaults = {
        open: true,
        title: 'Discard changes',
        message: 'Are you sure you want to continue?',
        okLabel: 'Discard',
        onCancel: () => undefined,
    };

    React.useEffect(
        () => {
            if (ProductId && ProductReceiptContext?.Warehouse && BinTracked && !initialbinloaded) {
                setInitialBinLoaded(true);
                fetchLineDetailsInitialBin?.({ ProductId, Warehouse: ProductReceiptContext?.Warehouse });
            }
        },
        [ProductId, ProductReceiptContext?.Warehouse, BinTracked, initialbinloaded]);

    const rightIcons = React.useMemo(
        () => [
            {
                label: 'Cancel',
                Icon: CancelIcon,
                iconStyle: { fill: 'rgba(178, 0, 0, 1)' },
                action: 'ProductReceiptLineDetailsCancel',
                invokeMouseDownCallback: true,
            },
            {
                label: 'OK',
                Icon: CheckCircle,
                action: 'ProductReceiptLineDetailsOk',
            },
        ],
        []
    );

    const centerIcons = React.useMemo(
        () => {
            return [
                {
                    label: 'Put away',
                    Icon: QuoteIcon,
                    action: 'ProductReceiptLineDetailsPutAway',
                    disabled: !productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineDetails?.EnablePutAway,
                },
            ];
        },
        [productReceiptLineDetailsData]
    );

    React.useEffect(
        () => {
            setActionBar({
                leftIcons: [],
                centerIcons: centerIcons,
                rightIcons
            });
        },
        [productReceiptLineDetailsData]
    );

    // Calculate line details
    const calculateLineDetail = async (name, value) => {
        try {
            if (!LineNumber || !ProductReceiptContext?.ProductReceiptId || productReceiptLineDetailsLoading ||
                productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineDetails?.[name] === value) {
                return null;
            }

            const response = await calculateLineDetailsMutation.mutateAsync({
                ProductReceiptLineDetails: { ...formValues, [name]: value },
                ProductReceiptId: ProductReceiptContext?.ProductReceiptId,
                BinLot: selectedBinLot || BinLot,
                LineNumber,
                urlQueryParams: {
                    ChangedField: name
                }
            });

            if (response?.Status) {
                setInternalDirty(true);
                setProductReceiptLineDetails(ProductReceiptContext?.ProductReceiptId, LineNumber, response);
            } else {
                resetForm();
            }

            return response;
        } catch (e) {

            return '';
        }

    };

    const calculateLineDetailPutAway = async (binLots) => {
        const response = await calculateLineDetailsMutation.mutateAsync({
            ProductReceiptLineDetails: { ...formValues },
            BinLot: binLots ?? [],
            ProductReceiptId: ProductReceiptContext?.ProductReceiptId,
            LineNumber,
            urlQueryParams: {
                ChangedField: 'LotSerial'
            }
        });

        if (response?.Status) {
            setSelectedBinLot(binLots);
            setInternalDirty(true);
            setAddSerialDialogOpen(false);
            setLotDialogOpen(false);
            setAddBinLocationDialogOpen(false);
            setProductReceiptLineDetails(ProductReceiptContext?.ProductReceiptId, LineNumber, response);
        } else {
            resetForm();
        }

        return response;
    };
    // Trigger this handler when field gets blur
    const formViewOnBlur = React.useCallback(
        async (e: any): Promise<void> => {
            const fieldName = e?.target?.name;
            const fieldValue = e?.target?.value;

            if (calculateLineDetailsMutation.isLoading) return;

            if (fieldName) {
                setInternalDirty(true);

                const data = productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineDetails;

                const isNotQuantityField = fieldName.includes('BuyUnit');
                const isValueChanged = !isNotQuantityField ? (parseFloat(fieldValue) ? (parseFloat(fieldValue) !== parseFloat(data?.[fieldName])) : parseFloat(data?.[fieldName]) !== 0) : (fieldValue !== data?.[fieldName]);

                const isInvalidQuantity = fieldValue === null || fieldValue === '';

                if (isValueChanged && !isInvalidQuantity) {
                    setInternalDirty(true);
                    await calculateLineDetail(fieldName, fieldValue);
                }
            }
        },
        [productReceiptLineDetailsData, calculateLineDetail]
    );

    const resetAndNavigateBack = (callBack: () => void) => {
        resetForm();
        destroyForm();
        callBack();
    };

    const updateProductReceiptLineDetails = React.useCallback(
        async () => {
            const { Status, ...filteredFormValues } = formValues;
            const response = await updateProductReceiptLineDetailsMutation.mutateAsync({
                ProductReceiptId: ProductReceiptContext?.ProductReceiptId,
                LineNumber,
                ProductReceiptLineDetails: { ...filteredFormValues },
                BinLot: selectedBinLot || BinLot
            });

            if (response?.Status) {
                setInternalDirty(false);
            }

            return response?.Status;
        },
        [formValues, updateProductReceiptLineDetailsMutation, ProductReceiptContext, selectedBinLot, BinLot]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            isChangesSavedOrDiscarded(): boolean {
                if (isDirty) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return !isDirty;
            },
            async ProductReceiptLineDetailsOk(navigateBack: () => void): Promise<void> {
                if (!productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineSummary?.PurchaseOrder && !formValues?.BuyUnit) {
                    showSnackBar({ variant: 'warning', message: 'Please enter Buy unit' });

                    return;
                }
                if (await updateProductReceiptLineDetails()) {
                    resetAndNavigateBack(navigateBack);
                }
            },
            ProductReceiptLineDetailsPutAway(): void {
                if (productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineDetails?.LotSerial === 'sf') {
                    setAddSerialDialogOpen(true);

                    return;
                }

                if (productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineDetails?.LotSerial === 'lf') {
                    setLotDialogOpen(true);

                    return;
                }
                setAddBinLocationDialogOpen(true);
            },
            ProductReceiptLineDetailsCancel(navigateBack: () => void): void {

                onCancelClicked.current = true;
                if (!isDirty) return resetAndNavigateBack(navigateBack);

                changeConfirmationDialog({
                    ...changeConfirmationDialogDefaults,
                    onOk: () => {
                        resetAndNavigateBack(navigateBack);
                    },
                    onCancel: () => {
                        onCancelClicked.current = false;
                    }
                });
            }
        }),
        [isDirty, formValues, ProductReceiptContext, selectedBinLot, BinLot, productReceiptLineDetailsData]
    );

    const lastCosttoggleChange = (event) => {
        setInternalDirty(true);
        calculateLineDetail('UpdateLastCost', event?.target?.checked);
    };

    const updateSupplierPricingtoggleChange = (event) => {
        setInternalDirty(true);
        calculateLineDetail('UpdateSupplierPricing', event?.target?.checked);
    };

    const applyDialog = (binLots) => {
        calculateLineDetailPutAway(binLots);
    };

    const applyLotDialog = (binLots) => {
        calculateLineDetailPutAway(binLots);
    };

    return (
        <React.Fragment>
            {addSerialDialogOpen && <AddSerialDialog
                open={addSerialDialogOpen}
                RequiredQuantity={Quantity}
                BinLots={selectedBinLot || BinLot}
                isBinTracked={BinTracked}
                ProductId={Number(ProductId)}
                WarehouseEntity={ProductReceiptContext?.Warehouse}
                defaultBin={lineDetailsInitialBin?.Bin?.inlineObject?.Bin}
                onApply={async (binLots) => {
                    applyDialog(binLots);
                }}
                onCancel={async () => { setAddSerialDialogOpen(false); }}
                calculateSerialNumber={calculateSerialNumberMutateAsync}
                DocumentType={'PR'}
            />}
            {lotDialogOpen && <AddLotDialog
                open={lotDialogOpen}
                title={'Enter Lot Numbers'}
                isBinTracked={BinTracked}
                LineNumber={LineNumber}
                defaultBin={lineDetailsInitialBin?.Bin?.inlineObject?.Bin}
                WarehouseEntity={ProductReceiptContext?.Warehouse}
                ProductId={Number(ProductId)}
                RequiredQuantity={Quantity}
                BinLots={selectedBinLot || BinLot}
                DocumentType={'PR'}
                onApply={async (binLots) => {
                    applyLotDialog(binLots);
                }}
                onCancel={async () => { setLotDialogOpen(false); }}
                config={{ bypassValidation: true }}
            />}
            {addBinLocationDialogOpen && <BinLocationsDialog
                open={addBinLocationDialogOpen}
                LineNumber={LineNumber}
                WarehouseEntity={ProductReceiptContext?.Warehouse}
                ProductId={Number(ProductId)}
                RequiredQuantity={Quantity}
                BinLots={selectedBinLot || BinLot}
                DocumentType={'PR'}
                onApply={async (binLots) => {
                    applyDialog(binLots);
                }}
                onCancel={async () => { setAddBinLocationDialogOpen(false); }}
            />}
            <div className={classes.formContainer}>
                <FormView
                    isLoading={productReceiptLineDetailsLoading || isProductReceiptLineDetailsFetching}
                    onBlur={calculateLineDetailsMutation.isLoading ? null : formViewOnBlur}
                    formName={'ProductReceiptLineDetailsForm'}
                    schema={ProductReceiptLineDetailsForm}
                    includeTabsHeight={false}
                    initialValues={productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineDetails}
                    valuesSchema={productReceiptLineDetailsData?.ProductReceiptLine?.schema?.ProductReceiptLineDetails}
                    operationMode={'EDIT'}
                    fieldExtensions={({
                        UpdateLastCost: {
                            onChange: lastCosttoggleChange
                        },
                        UpdateSupplierPricing: {
                            onChange: updateSupplierPricingtoggleChange
                        },
                        BuyUnit: {
                            disabled: productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineSummary?.PurchaseOrder,
                            required: !productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineSummary?.PurchaseOrder
                        },
                        UnitConversion: {
                            disabled: productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineSummary?.PurchaseOrder
                        }
                    })}
                    summaryTableRenderer={
                        (() => <SummaryTable
                            loading={false}
                            selectedProductReceiptLine={{
                                ...(productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineDetails ?? {}),
                                ...(productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLineSummary ?? {})
                            }}
                            ProductReceiptLinePricing={productReceiptLineDetailsData?.ProductReceiptLine?.inlineObject?.ProductReceiptLinePricing}
                            data={SummaryTableSchema}
                        />)}
                />
            </div>
        </React.Fragment>

    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(LineDetails)));
